import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { View, TouchableOpacity, StyleSheet, ScrollView, Platform, TextInput } from "react-native";

import { Text } from "react-native-paper";
import { useThemeSettings } from "../../../ThemeProvider";
import getGlobalStyles from "../../../globalStyles";
import { getContextualTags } from "../../../services/apiService";
import { debounce } from "../../../shared/helpers/utils";
import { GradientStrokeBox } from "../../../components/GradientStrokeBox";

interface TagInputFieldProps {
  selectedTags: any[];
  onChangeTags: (tags: any[]) => void;
  onFocusChange?: (focused: boolean) => void;
  singleSelect?: boolean
  initialSortPreference?: string
}

const TagInputField: React.FC<TagInputFieldProps> = ({
  selectedTags,
  onChangeTags,
  onFocusChange,
  singleSelect = false,
  initialSortPreference = "",
}) => {
  const [input, setInput] = useState("");

  const [sortPreference, setSortPreference] = useState("");
  const [tagError, setTagError] = useState(false);
  const [filteredTags, setFilteredTags] = useState<any[]>([]);
  const inputRef = useRef<TextInput>(null);
  const { t } = useTranslation();
  const { theme } = useThemeSettings();
  const gStyles = useMemo(() => getGlobalStyles(theme), [theme]);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const tagScrollRef = useRef<ScrollView>(null);
const [isFocused, setIsFocused] = useState(false);
  // Refs that always hold the latest values synchronously.
  // debouncedGetTags is memoized with [] deps so it permanently captures the
  // initial getTags closure — refs ensure it reads current values when it fires.
  const latestSelectedTagsRef = useRef<any[]>(selectedTags);
  const latestSortPrefRef = useRef<string>(sortPreference);
  useEffect(() => { latestSelectedTagsRef.current = selectedTags; }, [selectedTags]);
  useEffect(() => { latestSortPrefRef.current = sortPreference; }, [sortPreference]);

  const getTags = async (searchText?: string) => {
    const tagIds = singleSelect ? [] : latestSelectedTagsRef.current.map((t) => t.id);
    const sort = singleSelect
      ? (initialSortPreference || "Place")
      : (tagIds.length ? latestSortPrefRef.current : "");
    const tags = await getContextualTags(tagIds, searchText?.toLowerCase() || "", sort);
    const selectedIds = latestSelectedTagsRef.current.map((t) => t.id);
    setFilteredTags(tags.filter((t: any) => !selectedIds.includes(t.id)));
    setTagError(tags?.length === 0);
  };
  const debouncedGetTags = useMemo(() => debounce(getTags, 300), []);
  /* 🔹 Debounced search on input change */
  useEffect(() => {
    debouncedGetTags(input);
  }, [input, selectedTags]);
  /** Add tag */
  const addTag = useCallback(
    (tag: any) => {
      if (singleSelect) {
        latestSelectedTagsRef.current = [tag];
        onChangeTags([tag]);
        setInput("");
        setFilteredTags([]);
      } else if (!selectedTags?.find((t) => t?.id === tag?.id)) {
        const newTags = [...selectedTags, tag];
        latestSelectedTagsRef.current = newTags; // sync before state changes
        latestSortPrefRef.current = tag?.type;
        setSortPreference(tag?.type);
        onChangeTags(newTags);
      }
      setInput(""); // clear input
      inputRef.current?.focus();
    },
    [selectedTags, singleSelect],
  );

  /** Remove tag */
  const removeTag = useCallback(
    (tag: any) => {
      const newTags = selectedTags?.filter((t) => t?.id !== tag?.id);
      latestSelectedTagsRef.current = newTags; // sync before state change
      onChangeTags(newTags);
    },
    [selectedTags?.length],
  );

  /** Handle space or comma to create tag */
  const onSubmit = () => {
    const matchingTag = filteredTags.find(
      (tag: any) => tag?.name.toLowerCase() === input.trim().toLowerCase(),
    );
    if (matchingTag) {
      addTag(matchingTag);
    } else {
      setTagError(true);
    }
  };

  const suggestFilterAndSortTags = (tags: any[]) => {
    let sortedTags = tags?.map((t: any) => {
      return { ...t, name: denormalizeString(t.name) };
    });
    return sortedTags;
  };
  const denormalizeString = (str: string): string => {
    return str
      .split("_") // split by underscore
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // capitalize first letter
      .join(" "); // join with space
  };
  return (
    <View>
      {/* INPUT CONTAINER */}
      {/* <Text style={[styles.tagHeader, {color : theme.colors.primary,}]}>
        {title}
      </Text> */}
      <GradientStrokeBox
        stroke={3}
        wrapperStyle={{ borderRadius: 12 }}
        style={styles.inputContainer}>
      {singleSelect && selectedTags.length > 0 ? (
          // Single-select: show selected chip, hide input
          <View
            style={[
              styles.chip,
              {
                borderColor: theme.colors.primaryLight,
                backgroundColor: theme.colors.primaryLight2,
              },
            ]}
          >
            <Text style={[styles.chipText, { color: theme.colors.textPrimary }]}>
              {denormalizeString(selectedTags[0].name)}
            </Text>
            <TouchableOpacity onPress={() => removeTag(selectedTags[0])}>
              <Text style={[styles.remove, { color: theme.colors.textPrimary }]}>✕</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <ScrollView
            ref={tagScrollRef}
            horizontal={false}
            contentContainerStyle={[{ flexGrow: 1, flexDirection: "row", gap: 5, flexWrap: "wrap", alignItems: "center"}, (Platform.OS === "web" && selectedTags.length > 0) && {paddingHorizontal : 10, paddingVertical : 10}]}
            style={{ maxHeight: 100, flex: 1 }}
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
            onContentSizeChange={() => {
              tagScrollRef.current?.scrollToEnd({ animated: true });
            }}
          >
            {!singleSelect && selectedTags?.map((tag) => (
              <View
                key={tag?.id}
                style={[
                  styles.chip,
                  {
                    borderColor: theme.colors.primaryLight,
                    backgroundColor: theme.colors.primaryLight3,
                  },
                ]}
              >
               <Text style={[styles.chipText, { color: theme.colors.primary }]}>
                  {tag.name}
              </Text>
              <TouchableOpacity onPress={() => removeTag(tag)}>
                  <Text style={[styles.remove, { color: theme.colors.primary }]}>✕</Text>
                </TouchableOpacity>
              </View>
            ))}
            {/* Editable Input */}
            <TextInput
              ref={inputRef}
              value={input}
              onChangeText={(val) => {
                setTagError(false);
                setInput(val);
              }}
              style={[
                styles.textInput,
                {
                  flexGrow: 1,
                  flexShrink: 1,
                  flexBasis: "auto",
                  paddingLeft: Platform.OS === "web" ? 10 : 0,
                },
                { backgroundColor: "transparent", color: theme?.colors?.primary },
                tagError && styles.textError,
              ]}
              placeholder={selectedTags.length === 0 ? t("screens.collections.tagName") : ""}
              placeholderTextColor={theme?.colors?.primaryLight}
              onSubmitEditing={onSubmit}
              returnKeyType="done"
              onFocus={() => {
                setTimeout(() => {
                  setIsFocused(true);
                  onFocusChange?.(true);
                }, 100);
              }}
              onBlur={() => {
                setTimeout(() => {
                  setIsFocused(false);
                  onFocusChange?.(false);
                }, 50);
              }}
            />
          </ScrollView>
        )}
      </GradientStrokeBox>

      {/* SUGGESTED TAGS */}
      {(input.length > 0 || (!singleSelect && selectedTags.length > 0)) && (
        <View style={{ marginTop: 10 }}>
        {/* <Text style={[{ color: theme?.colors?.primary, marginBottom: 16 }]}>
          {t("screens.collections.suggestedTags")}
        </Text> */}

        <ScrollView
          horizontal={false}
          contentContainerStyle={styles.tagRow}
          style={{ maxHeight: 90 }}
          showsVerticalScrollIndicator={false}
          nestedScrollEnabled={true}
          scrollEnabled={true} 
          keyboardShouldPersistTaps="always"
          keyboardDismissMode="none"
        >
          {suggestFilterAndSortTags(filteredTags)?.map((tag, index) => {
            const ids = selectedTags?.map((s) => s.id);
            const selected: any = ids.includes(tag.id);

            return (
              <TouchableOpacity
                key={tag.id}
                disabled={selected}
                onPress={() => addTag(tag)}
                delayPressIn={50}
                style={{ flexDirection: "row", gap: 6, flexWrap: "nowrap" }}
              >
                <Text
                  accessibilityRole="link"
                  style={[gStyles.link, { color: theme?.colors?.textPrimary, fontSize : 14 }]}
                >
                  {tag.name}
                </Text>
                {index + 1 !== filteredTags.length && (
                  <Text style={{ color: theme?.colors?.primary }}>|</Text>
                )}
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View> )}
    </View>
  );
};

export default TagInputField;

const styles = StyleSheet.create({
  inputContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 14,
    paddingHorizontal: 14,
    backgroundColor: "transparent",
  },
  tagHeader : { paddingBottom : 10, fontWeight : '500', fontSize : 14, lineHeight : 20, letterSpacing : 1.4},
  tagRow: {
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 6,
},
  chip: {
    flexDirection: "row",
    borderWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 20,
    alignItems: "center",
  },
  chipText: {
    marginRight: 6,
    fontSize: 14,
  },
  remove: {
    fontSize: 14,
  },
  textInput: {
    minWidth: 60,
    maxWidth: "100%",
    fontSize: 12,
    paddingVertical: 0,
    outlineWidth: 0,
    outlineStyle: "none",
    outlineColor: "transparent",
    borderWidth: 0,
    height: 32,
    textAlignVertical: "center",
    includeFontPadding: false,
  } as any,
  textError: {
    borderWidth: 1,
    borderColor: "red",
    paddingHorizontal: 10,
  },
});
